import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { fetchPages, upsertPage } from "../../redux/blogSlice";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bold, Italic, Underline, Link, Image, List,
  Heading, CheckCircle, AlertCircle, Loader2,
  ChevronDown, ChevronUp, X, Minus, Eye, EyeOff,
  FileText, ExternalLink,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME
───────────────────────────────────────────────────────── */
const C = {
  paper:        "#ffffff",
  surface:      "#f7f7f7",
  ink:          "#0a0a0a",
  inkSoft:      "#2a2a2a",
  inkMid:       "#6b6b6b",
  inkLight:     "#a3a3a3",
  border:       "#e0e0e0",
  success:      "#1a7a3a",
  successBg:    "#f0faf4",
  successBorder:"#b8dfc8",
  danger:       "#b22222",
  dangerBg:     "#fff5f5",
  dangerBorder: "#f0c8c8",
  serif:        "'Cormorant Garamond', Georgia, serif",
  sans:         "'Montserrat', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   RICH TEXT PREVIEW RENDERER
   Parses **bold**, _italic_, [text](url), ## headings,
   - bullets, bare https:// links, email@domain
───────────────────────────────────────────────────────── */
const renderRichLine = (text: string, key: number | string): React.ReactNode => {
  const regex = /(\*\*(.+?)\*\*|_(.+?)_|\[(.+?)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}))/g;
  const parts: React.ReactNode[] = [];
  let last = 0; let m: RegExpExecArray | null; let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(<span key={k++}>{text.slice(last, m.index)}</span>);
    if (m[2])      parts.push(<strong key={k++} style={{ fontWeight:600, color:C.ink }}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={k++} style={{ fontStyle:"italic" }}>{m[3]}</em>);
    else if (m[4]) parts.push(<a key={k++} href={m[5]} target="_blank" rel="noopener noreferrer" style={linkSt}>{m[4]}</a>);
    else if (m[6]) parts.push(<a key={k++} href={m[6]} target="_blank" rel="noopener noreferrer" style={{ ...linkSt, wordBreak:"break-all", fontSize:11 }}>{m[6]}</a>);
    else if (m[7]) parts.push(<a key={k++} href={`mailto:${m[7]}`} style={linkSt}>{m[7]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<span key={k++}>{text.slice(last)}</span>);
  return <React.Fragment key={key}>{parts.length ? parts : text}</React.Fragment>;
};
const linkSt: React.CSSProperties = { color:C.ink, fontWeight:500, textDecoration:"underline", textUnderlineOffset:3, textDecorationColor:"#cacaca" };

const renderRichContent = (raw: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const lines = raw.split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) { nodes.push(<div key={`sp${i}`} style={{ height:8 }}/>); i++; continue; }
    if (/^#\s/.test(line)) { nodes.push(<h2 key={`h1${i}`} style={{ fontFamily:C.serif, fontSize:"clamp(24px,3vw,32px)", fontWeight:600, color:C.ink, lineHeight:1.15, margin:"24px 0 10px", letterSpacing:"-0.01em" }}>{renderRichLine(line.replace(/^#+\s/,""),`h${i}`)}</h2>); i++; continue; }
    if (/^#{2}\s/.test(line)) { nodes.push(<h3 key={`h2${i}`} style={{ fontFamily:C.serif, fontSize:"clamp(18px,2.5vw,24px)", fontWeight:600, color:C.ink, lineHeight:1.2, margin:"20px 0 8px" }}>{renderRichLine(line.replace(/^#+\s/,""),`h2${i}`)}</h3>); i++; continue; }
    if (/^#{3}\s/.test(line)) { nodes.push(<h4 key={`h3${i}`} style={{ fontFamily:C.sans, fontSize:11, fontWeight:700, color:C.ink, letterSpacing:"0.14em", textTransform:"uppercase", margin:"18px 0 6px" }}>{renderRichLine(line.replace(/^#+\s/,""),`h3${i}`)}</h4>); i++; continue; }
    if (/^[-*•]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*•]\s/.test(lines[i].trim())) { items.push(lines[i].replace(/^[-*•]\s/,"")); i++; }
      nodes.push(<ul key={`ul${i}`} style={{ margin:"10px 0 14px", paddingLeft:0, listStyle:"none", display:"flex", flexDirection:"column", gap:7 }}>{items.map((item,idx) => <li key={idx} style={{ display:"flex", gap:10, alignItems:"flex-start" }}><span style={{ width:4, height:4, borderRadius:"50%", background:C.inkMid, marginTop:9, flexShrink:0 }}/><span style={{ fontSize:13, lineHeight:1.75, color:C.inkSoft, fontFamily:C.sans, fontWeight:300 }}>{renderRichLine(item,`li${idx}`)}</span></li>)}</ul>);
      continue;
    }
    if (/^---+$/.test(trimmed)) { nodes.push(<div key={`hr${i}`} style={{ height:1, background:C.border, margin:"20px 0" }}/>); i++; continue; }
    if (/^>\s/.test(line)) { nodes.push(<blockquote key={`bq${i}`} style={{ borderLeft:`2px solid ${C.ink}`, margin:"16px 0", paddingLeft:16, paddingTop:3, paddingBottom:3 }}><p style={{ fontFamily:C.serif, fontSize:17, fontStyle:"italic", color:C.inkSoft, lineHeight:1.7, margin:0 }}>{renderRichLine(line.replace(/^>\s/,""),`bq${i}`)}</p></blockquote>); i++; continue; }
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^[#>\-*•]/.test(lines[i]) && !/^---+$/.test(lines[i].trim())) { paraLines.push(lines[i]); i++; }
    if (paraLines.length) nodes.push(<p key={`p${i}`} style={{ fontSize:13, lineHeight:1.85, color:C.inkSoft, fontFamily:C.sans, fontWeight:300, margin:"0 0 12px" }}>{paraLines.map((pl,pi) => <React.Fragment key={pi}>{renderRichLine(pl,`pl${i}-${pi}`)}{pi<paraLines.length-1 && " "}</React.Fragment>)}</p>);
  }
  return nodes;
};

/* ─────────────────────────────────────────────────────────
   TOOLBAR BUTTON
───────────────────────────────────────────────────────── */
const TBtn: React.FC<{ title:string; onClick:(e:React.MouseEvent)=>void; active?:boolean; children:React.ReactNode }> = ({ title, onClick, active, children }) => (
  <button title={title} onMouseDown={e => { e.preventDefault(); onClick(e); }}
    style={{ ...S.tbtn, background:active?C.ink:"transparent", color:active?"#fff":C.inkMid, border:`1px solid ${active?C.ink:C.border}` }}
    className="ep-tbtn">
    {children}
  </button>
);
const TDivider = () => <div style={{ width:1, height:20, background:C.border, margin:"0 2px", flexShrink:0 }}/>;

/* ─────────────────────────────────────────────────────────
   RICH EDITOR COMPONENT
───────────────────────────────────────────────────────── */
const RichEditor: React.FC<{ value: string; onChange: (v:string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => {
  const taRef   = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);
  const [linkPopup, setLinkPopup] = useState(false);
  const [linkUrl,   setLinkUrl]   = useState("");
  const linkRef = useRef<HTMLInputElement>(null);

  const wrap = (before: string, after: string, sample: string) => {
    const ta = taRef.current; if (!ta) return;
    const start = ta.selectionStart; const end = ta.selectionEnd;
    const sel  = value.slice(start, end) || sample;
    onChange(value.slice(0, start) + before + sel + after + value.slice(end));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + before.length, start + before.length + sel.length); }, 10);
  };
  const insertPrefix = (prefix: string) => {
    const ta = taRef.current; if (!ta) return;
    const start     = ta.selectionStart;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const next      = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + prefix.length, start + prefix.length); }, 10);
  };
  const handleLinkInsert = () => {
    if (!linkUrl.trim()) return;
    const ta = taRef.current; if (!ta) return;
    const start = ta.selectionStart; const end = ta.selectionEnd;
    const label = value.slice(start, end) || "link text";
    onChange(value.slice(0, start) + `[${label}](${linkUrl})` + value.slice(end));
    setLinkUrl(""); setLinkPopup(false);
    setTimeout(() => taRef.current?.focus(), 10);
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:3, padding:"7px 10px", background:C.surface, border:`1px solid ${C.border}`, borderBottom:"none" }}>
        <TBtn title="Bold (**text**)"   onClick={() => wrap("**","**","bold text")}><Bold size={12}/></TBtn>
        <TBtn title="Italic (_text_)"   onClick={() => wrap("_","_","italic text")}><Italic size={12}/></TBtn>
        <TBtn title="Underline"         onClick={() => wrap("<u>","</u>","underline")}><Underline size={12}/></TBtn>
        <TDivider/>
        <TBtn title="Heading H2"        onClick={() => insertPrefix("## ")}><span style={{ fontSize:9, fontWeight:700 }}>H2</span></TBtn>
        <TBtn title="Heading H3"        onClick={() => insertPrefix("### ")}><span style={{ fontSize:9, fontWeight:700 }}>H3</span></TBtn>
        <TBtn title="Bullet list"       onClick={() => insertPrefix("- ")}><List size={12}/></TBtn>
        <TDivider/>
        {/* Link */}
        <div style={{ position:"relative" }}>
          <TBtn title="Insert link" onClick={() => { setLinkPopup(v=>!v); setTimeout(()=>linkRef.current?.focus(),40); }} active={linkPopup}><Link size={12}/></TBtn>
          <AnimatePresence>
            {linkPopup && (
              <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                style={{ position:"absolute", top:"110%", left:0, zIndex:200, display:"flex", gap:6, alignItems:"center", background:C.paper, border:`1px solid ${C.border}`, padding:"7px 10px", boxShadow:"0 4px 20px rgba(0,0,0,0.08)", minWidth:280 }}>
                <input ref={linkRef} type="url" placeholder="https://example.com" value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)} onKeyDown={e => e.key==="Enter" && handleLinkInsert()}
                  style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, padding:"7px 10px", fontSize:11, color:C.ink, fontFamily:C.sans, outline:"none" }}/>
                <button onClick={handleLinkInsert} style={{ background:C.ink, color:"#fff", border:"none", padding:"7px 12px", fontSize:9, fontWeight:700, letterSpacing:"0.14em", cursor:"pointer", fontFamily:C.sans }}>INSERT</button>
                <button onClick={()=>setLinkPopup(false)} style={{ background:"transparent", border:`1px solid ${C.border}`, width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:C.inkMid }}><X size={11}/></button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <TBtn title="Blockquote (> text)" onClick={() => insertPrefix("> ")}><span style={{ fontSize:11, fontStyle:"italic" }}>"</span></TBtn>
        <TBtn title="Divider (---)"       onClick={() => { onChange(value + "\n---\n"); setTimeout(() => taRef.current?.focus(), 10); }}><Minus size={12}/></TBtn>
        <div style={{ flex:1 }}/>
        <TBtn title={preview ? "Edit" : "Preview"} onClick={() => setPreview(v=>!v)} active={preview}>
          {preview ? <EyeOff size={12}/> : <Eye size={12}/>}
        </TBtn>
      </div>

      {/* Editor / Preview */}
      {preview ? (
        <div style={{ minHeight:200, padding:"16px 14px", border:`1px solid ${C.border}`, background:C.paper, overflowY:"auto" }}>
          {value.trim() ? renderRichContent(value) : <span style={{ fontSize:11, color:C.inkLight, fontStyle:"italic" }}>Nothing to preview yet…</span>}
        </div>
      ) : (
        <textarea ref={taRef} value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || "Write page content here…\n\n## Section Heading\n\nParagraph text with **bold**, _italic_, or [links](https://).\n\n- Bullet point"}
          style={{ width:"100%", minHeight:220, background:C.paper, border:`1px solid ${C.border}`, padding:"14px", fontSize:13, color:C.ink, lineHeight:1.75, fontFamily:C.sans, fontWeight:300, resize:"vertical", outline:"none", boxSizing:"border-box" as const, transition:"border-color 0.2s" }}
          className="ep-input"
        />
      )}

      {/* Syntax hints */}
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", padding:"5px 0 2px" }}>
        {[["**bold**","bold"],["_italic_","italic"],["[text](url)","link"],["## Heading","heading"],["- item","bullet"],['> "quote"',"quote"]].map(([syn,lbl]) => (
          <span key={lbl} style={{ fontSize:9, color:C.inkLight }}>
            <code style={{ background:C.surface, padding:"1px 5px", fontFamily:"monospace", fontSize:9 }}>{syn}</code> {lbl}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const EPages: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const pages    = useSelector((state: RootState) => state.blog.pages);

  const [title,      setTitle]      = useState("");
  const [slug,       setSlug]       = useState("");
  const [content,    setContent]    = useState("");
  const [loading,    setLoading]    = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [toast,      setToast]      = useState<{ type:"success"|"danger"; text:string } | null>(null);
  const [expanded,   setExpanded]   = useState<Record<string,boolean>>({});

  useEffect(() => { dispatch(fetchPages()); }, [dispatch]);

  /* ── Slug auto-gen (original logic preserved) ── */
  useEffect(() => {
    if (slugEdited) return;
    setSlug(title.toLowerCase().trim().replace(/[^\w\s-]/g,"").replace(/\s+/g,"-"));
  }, [title, slugEdited]);

  const flash = (type:"success"|"danger", text:string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { flash("danger","Please add a page title."); return; }
    if (!content.trim()) { flash("danger","Please add some content."); return; }
    setLoading(true);
    try {
      await dispatch(upsertPage({ title, slug, content })).unwrap();
      setTitle(""); setSlug(""); setContent(""); setSlugEdited(false);
      flash("success", `Page "${title}" published successfully!`);
      dispatch(fetchPages());
    } catch (err: any) {
      flash("danger", err?.message || "Failed to publish page.");
    }
    setLoading(false);
  };

  const toggleExpand = (id: string) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={{ background:C.paper, fontFamily:C.sans, color:C.ink }}>
      <style>{FONT_CSS + EPAGES_CSS}</style>

      {/* ── TITLE STRIP (slim — embedded inside Editorial shell) ── */}
      <div style={S.titleStrip}>
        <div style={{ height:2, background:C.ink, marginBottom:16 }}/>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          <div>
            <p style={S.headerEye}>CONTENT MANAGEMENT</p>
            <h1 style={S.headerTitle}>Add Page</h1>
          </div>
          <span style={S.pageCount}>{pages.length} page{pages.length !== 1 ? "s" : ""} published</span>
        </div>
        <div style={{ height:1, background:C.border }}/>
      </div>

      <div style={S.body} className="ep-body-wrap">

        {/* ── TOAST ── */}
        <AnimatePresence>
          {toast && (
            <motion.div key="toast" initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", marginBottom:24, background:toast.type==="success"?C.successBg:C.dangerBg, border:`1px solid ${toast.type==="success"?C.successBorder:C.dangerBorder}` }}>
              {toast.type==="success" ? <CheckCircle size={14} color={C.success}/> : <AlertCircle size={14} color={C.danger}/>}
              <span style={{ fontSize:12, fontWeight:500, color:toast.type==="success"?C.success:C.danger, flex:1 }}>{toast.text}</span>
              <button onClick={() => setToast(null)} style={{ background:"none", border:"none", cursor:"pointer", color:C.inkMid }}><X size={12}/></button>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={S.layout} className="ep-layout">

          {/* ══════════════════════════════
              LEFT — FORM
          ══════════════════════════════ */}
          <form onSubmit={handleSubmit} style={S.form}>

            <div style={S.sectionHead}>
              <div style={{ width:24, height:2, background:C.ink, flexShrink:0 }}/>
              <h2 style={S.sectionTitle}>Create New Page</h2>
            </div>

            {/* Title */}
            <div style={S.fieldGroup}>
              <label style={S.label}>PAGE TITLE <span style={{ color:C.danger }}>*</span></label>
              <input type="text" placeholder="e.g. About Us, Privacy Policy…"
                value={title} onChange={e => setTitle(e.target.value)}
                style={S.titleInput} className="ep-input ep-title"/>
            </div>

            {/* Slug */}
            <div style={S.fieldGroup}>
              <label style={S.label}>URL SLUG</label>
              <div style={S.slugWrap}>
                <span style={S.slugPrefix}>site.com/</span>
                <input type="text" placeholder="auto-generated" value={slug}
                  onChange={e => { setSlug(e.target.value.toLowerCase().replace(/[^\w-]/g,"-")); setSlugEdited(true); }}
                  style={S.slugInput} className="ep-input"/>
                {slugEdited && (
                  <button type="button" onClick={() => { setSlug(title.toLowerCase().trim().replace(/[^\w\s-]/g,"").replace(/\s+/g,"-")); setSlugEdited(false); }}
                    style={S.slugReset}>Reset</button>
                )}
              </div>
            </div>

            {/* Content */}
            <div style={S.fieldGroup}>
              <label style={S.label}>CONTENT <span style={{ color:C.danger }}>*</span></label>
              <RichEditor
                value={content} onChange={setContent}
                placeholder={"Write your page content here…\n\n## Section Heading\n\nYour paragraph text. Supports **bold**, _italic_, [links](https://), and more.\n\n- Bullet point one\n- Bullet point two"}
              />
            </div>

            {/* Submit */}
            <motion.button whileTap={{ scale:0.97 } as any} type="submit" disabled={loading}
              style={{ ...S.publishBtn, opacity:loading?0.65:1, cursor:loading?"not-allowed":"pointer" }} className="ep-publish-btn">
              {loading
                ? <><Loader2 size={14} className="ep-spin" style={{ marginRight:8 }}/> SAVING…</>
                : "PUBLISH PAGE"
              }
            </motion.button>

          </form>

          {/* ══════════════════════════════
              RIGHT — PUBLISHED PAGES
          ══════════════════════════════ */}
          <div style={S.sidebar}>
            <div style={S.sectionHead}>
              <div style={{ width:24, height:2, background:C.ink, flexShrink:0 }}/>
              <h2 style={S.sectionTitle}>Published Pages</h2>
            </div>

            {pages.length === 0 ? (
              <div style={S.empty}>
                <FileText size={28} color={C.border} strokeWidth={1}/>
                <p style={{ fontSize:13, color:C.inkLight, marginTop:10, fontFamily:C.sans }}>No pages yet</p>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                {pages.map((p, idx) => (
                  <div key={p.id} style={{ borderTop:`1px solid ${C.border}`, borderBottom: idx===pages.length-1?`1px solid ${C.border}`:"none" }}>
                    {/* Row */}
                    <div style={S.pageRow} onClick={() => toggleExpand(p.id)} className="ep-row ep-page-row">
                      <div style={{ display:"flex", alignItems:"center", gap:12, flex:1, minWidth:0 }}>
                        <span style={S.rowNum}>{String(idx+1).padStart(2,"0")}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={S.rowTitle}>{p.title}</div>
                          <div style={S.rowSlug}>/{p.slug}</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <a href={`/${p.slug}`} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ width:26, height:26, display:"flex", alignItems:"center", justifyContent:"center", border:`1px solid ${C.border}`, color:C.inkMid, textDecoration:"none" }}
                          title="View page">
                          <ExternalLink size={11}/>
                        </a>
                        <button style={{ ...S.iconBtn }} onClick={e => { e.stopPropagation(); toggleExpand(p.id); }} title={expanded[p.id]?"Collapse":"Expand"}>
                          {expanded[p.id] ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                        </button>
                      </div>
                    </div>

                    {/* Expanded preview */}
                    <AnimatePresence>
                      {expanded[p.id] && (
                        <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }} exit={{ height:0, opacity:0 }} transition={{ duration:0.22 }}
                          style={{ overflow:"hidden" }}>
                          <div style={{ padding:"14px 0 18px", borderTop:`1px solid ${C.border}` }}>
                            {renderRichContent(
                              p.content.length > 400 ? p.content.slice(0, 400) + "…" : p.content
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default EPages;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  /* Title strip — no sticky, no fixed height, embeds cleanly in shell */
  titleStrip:  { padding:"20px 20px 0" },
  headerEye:   { fontSize:9, letterSpacing:"0.3em", color:C.inkMid, fontFamily:C.sans, marginBottom:6 },
  headerTitle: { fontFamily:C.serif, fontSize:"clamp(26px,4vw,42px)" as any, fontWeight:600, color:C.ink, lineHeight:1, margin:0, letterSpacing:"-0.01em" },
  pageCount:   { fontSize:10, letterSpacing:"0.14em", color:C.inkMid, fontFamily:C.sans, paddingBottom:4 },

  /* Body — padding handled via CSS class for mobile override */
  body:   { maxWidth:1160, margin:"0 auto", padding:"28px 20px 60px" },
  /* Layout — CSS class ep-layout handles breakpoint, inline style is desktop default */
  layout: { display:"grid", gridTemplateColumns:"1fr 340px", gap:32, alignItems:"start" },

  form:        { display:"flex", flexDirection:"column", gap:20 },
  sectionHead: { display:"flex", alignItems:"center", gap:14, marginBottom:4 },
  sectionTitle:{ fontFamily:C.serif, fontSize:22, fontWeight:600, color:C.ink, margin:0 },

  fieldGroup:  { display:"flex", flexDirection:"column", gap:7 },
  label: { fontSize:9, letterSpacing:"0.22em", color:C.inkMid, fontFamily:C.sans, fontWeight:600 },

  titleInput: { width:"100%", background:C.paper, border:`1px solid ${C.border}`, padding:"14px 16px", fontSize:20, fontWeight:600, color:C.ink, fontFamily:C.serif, outline:"none", boxSizing:"border-box" as const, letterSpacing:"-0.01em", transition:"border-color 0.2s" },
  slugWrap:   { display:"flex", alignItems:"center", border:`1px solid ${C.border}`, background:C.paper },
  slugPrefix: { fontSize:12, color:C.inkLight, padding:"0 12px", flexShrink:0, borderRight:`1px solid ${C.border}`, lineHeight:"42px" },
  slugInput:  { flex:1, background:"transparent", border:"none", outline:"none", padding:"11px 12px", fontSize:12, color:C.ink, fontFamily:"'Courier New',monospace", letterSpacing:"0.02em" },
  slugReset:  { fontSize:9, color:C.inkMid, letterSpacing:"0.1em", background:"none", border:"none", cursor:"pointer", padding:"0 12px", fontFamily:C.sans, flexShrink:0 },

  tbtn: { width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", transition:"all 0.15s", flexShrink:0 },

  publishBtn: { display:"inline-flex", alignItems:"center", justifyContent:"center", background:C.ink, color:"#fff", border:"none", padding:"14px 32px", fontSize:11, fontWeight:700, letterSpacing:"0.22em", fontFamily:C.sans, alignSelf:"flex-start" as const, transition:"opacity 0.2s" },

  /* Sidebar */
  sidebar: { position:"sticky" as const, top:20 },
  empty:   { display:"flex", flexDirection:"column", alignItems:"center", padding:"40px 20px", textAlign:"center" as const },

  /* Page rows */
  pageRow: { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"15px 0", gap:10, cursor:"pointer" },
  rowNum:  { fontSize:10, color:C.border, letterSpacing:"0.08em", fontFamily:C.sans, fontWeight:500, flexShrink:0, width:24 },
  rowTitle:{ fontSize:14, fontWeight:500, color:C.ink, fontFamily:C.serif, letterSpacing:"0.01em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" as const },
  rowSlug: { fontSize:10, color:C.inkLight, marginTop:2, fontFamily:"'Courier New',monospace" },
  iconBtn: { background:"transparent", border:`1px solid ${C.border}`, width:26, height:26, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:C.inkMid },
};

const EPAGES_CSS = `
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; }

  .ep-input:focus { border-color: #0a0a0a !important; }
  .ep-title:focus { border-color: #0a0a0a !important; }
  input::placeholder  { color: #a3a3a3; font-size: 12px; }
  textarea::placeholder { color: #a3a3a3; font-size: 12px; line-height: 1.75; }
  .ep-tbtn:hover { border-color: #0a0a0a !important; color: #0a0a0a !important; }
  .ep-row:hover  { background: #f7f7f7; }

  @keyframes ep-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  .ep-spin { animation: ep-spin 1s linear infinite; }

  /* ── Desktop: two-column layout ── */
  .ep-layout {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 32px;
    align-items: start;
  }

  /* ── Mobile: single column, tighter spacing ── */
  @media (max-width: 700px) {
    .ep-layout {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
    }

    /* Sidebar unsticks so it flows below the form */
    .ep-layout > div:last-child {
      position: relative !important;
      top: 0 !important;
    }

    /* Tighter body padding on small screens */
    .ep-body-wrap { padding: 20px 14px 60px !important; }

    /* Slug prefix: shorter so it doesn't crowd the input */
    .ep-slug-prefix { display: none; }

    /* Toolbar wraps more gracefully */
    .ep-toolbar { gap: 2px !important; }

    /* Title font scale */
    .ep-page-title { font-size: 26px !important; }

    /* Published pages list — tighter rows */
    .ep-page-row { padding: 12px 0 !important; }

    /* Publish button: full width on mobile */
    .ep-publish-btn { width: 100% !important; justify-content: center; }
  }
`;